// React
import { lazy, Suspense, useState } from "react";
// MUI
import { Skeleton, Stack, Typography } from "@mui/material";

// Context
import { CrossStoreReconciliationContextProvider } from "@DataPages/context/salesOperations/salesPerformance/CrossStoreReconciliationContext.tsx";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Helpers
import { getPreviousMonthName } from "@Common/helpers/Dates/date";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import SalesPerformanceTable from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/Tables/SalesPerformanceTable";
import PayCrossStoreCommissionsButton from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/components/CrossStoreCommissionsDialog/Widgets/PayCrossStoreCommissionsButton";
// Assets
import emptyTable from "@/assets/images/commons/empty_table.png";
import empty from "@/assets/images/commons/empty.png";

// Types
import { Nullable } from "@Common/interfaces/types.ts";
import { ISidebarItem } from "@Common/interfaces/sidebar";
import { ICarryOverFundingProgress } from "@DataPages/types/salesOperations/salesPerformance/model.ts";
import { ISalesPerformance } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance.ts";

const CrossStoreCommissionsDialog = lazy(
	() =>
		import(
			"@DataPages/components/SalesOperations/Tabs/SalesPerformance/components/CrossStoreCommissionsDialog/CrossStoreCommissionsDialog"
		)
);

type CarryOverFundingSectionProps = {
	isLoading: boolean;
	selected: Nullable<ISidebarItem>;
	data: ICarryOverFundingProgress[] | undefined;
};
export default function CarryOverFundingSection(props: Readonly<CarryOverFundingSectionProps>) {
	const { isLoading, selected: selectedCommissionCycle, data: carryOverFundingProgress } = props;

	const locationId = useLocationId();

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	/********************************************* HANDLERS ************************************************/
	function handleDialogOpen() {
		setIsDialogOpen(true);
	}

	function handleDialogClose() {
		setIsDialogOpen(false);
	}

	if (isLoading) {
		return (
			<Stack spacing={4}>
				<Stack direction={"row"} justifyContent={"space-between"} alignContent={"center"}>
					<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
					<Skeleton variant="rectangular" height={25} width={150} sx={{ borderRadius: "6px" }} />
				</Stack>
				<TableLoader content multiTitles={5} rows={7} />
			</Stack>
		);
	}

	if (!selectedCommissionCycle) {
		return (
			<EmptyBody
				image={empty}
				title={"No Carry Overs selected"}
				bodyHeight={"510px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}

	if (!carryOverFundingProgress?.length) {
		return (
			<EmptyBody
				image={emptyTable}
				title={"No Carry Overs to show"}
				bodyHeight={"510px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}

	return (
		<Stack>
			<Stack direction={"row"} component={"header"} justifyContent={"space-between"} alignContent={"center"}>
				<Typography component={"h2"} variant={"h2"}>{`${getPreviousMonthName(
					selectedCommissionCycle?.startDate
				)} Carry Over Funding In Progress`}</Typography>
				<PayCrossStoreCommissionsButton
					isDataLoading={isLoading}
					isExportable={selectedCommissionCycle?.status === "CLOSING"}
					// Callbacks
					onClick={handleDialogOpen}
				/>
			</Stack>

			<SalesPerformanceTable
				rows={carryOverFundingProgress as unknown as ISalesPerformance[]}
				tableKey={"carry-over"}
				useSwitch={false}
				locationId={locationId}
			/>
			{isDialogOpen && (
				<Suspense>
					<CrossStoreReconciliationContextProvider locationId={locationId}>
						<CrossStoreCommissionsDialog
							// Data
							selectedCommissionCycle={selectedCommissionCycle}
							// State
							open={isDialogOpen}
							// Callbacks
							onCloseDialog={handleDialogClose}
						/>
					</CrossStoreReconciliationContextProvider>
				</Suspense>
			)}
		</Stack>
	);
}
